/**
 * Reading a callable's validation error (Oct 2026): which fields the server rejected and a plain message, so a form
 * can mark the field in red ("Field required") instead of printing raw validator JSON. Server side and shapes:
 * shared/src/inputErrors.ts. Components: components/FieldError.tsx (FieldError, ErrorNote, invalidCls).
 */
import {
  invalidInputMessage,
  isInvalidInputDetails,
  issuesFromLegacyMessage,
  type InputFieldIssue,
} from '@daaily/shared';

const rawMessage = (e: unknown) => (e instanceof Error ? e.message : typeof e === 'string' ? e : '');

/** The fields the server rejected, or null when the error is not a validation error. */
export function inputIssues(e: unknown): InputFieldIssue[] | null {
  const details = (e as { details?: unknown } | null)?.details;
  if (isInvalidInputDetails(details)) return details.fields;
  // A server not yet redeployed still sends the raw validator JSON as the message.
  return issuesFromLegacyMessage(rawMessage(e));
}

/** The rejected field at `path`, or one below it ("lines" matches "lines.2.amount"). */
export function issueAt(issues: readonly InputFieldIssue[] | null | undefined, path: string): InputFieldIssue | undefined {
  return issues?.find((i) => i.path === path || i.path.startsWith(`${path}.`));
}

/** A message fit for people: the plain validation text, else the error's own message, else `fallback`. */
export function errorText(e: unknown, fallback = 'Something went wrong.'): string {
  const legacy = issuesFromLegacyMessage(rawMessage(e));
  if (legacy) return invalidInputMessage(legacy);
  return rawMessage(e) || fallback;
}

/** Technical detail for the (i) next to an error: the error code plus each field's validator detail. */
export function errorDetail(e: unknown): string | null {
  const code = (e as { code?: unknown } | null)?.code;
  const issues = inputIssues(e);
  const parts = [
    typeof code === 'string' ? code : '',
    ...(issues ?? []).map((i) => `${i.path || '(request)'}: ${i.detail}`),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
